import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import '../styles.css';

const STATUTS = [
  { value: 'contact',           label: 'Contact',           color: '#8e8e93', bg: '#f2f2f7' },
  { value: 'invite_0',          label: 'Invité 0',          color: '#b45309', bg: '#fff3e0' },
  { value: 'invite_1',          label: 'Invité 1',          color: '#b45309', bg: '#fff3e0' },
  { value: 'invite_2',          label: 'Invité 2',          color: '#f59e0b', bg: '#fef3c7' },
  { value: 'attente_paiement',  label: 'Attente paiement',  color: '#7c3aed', bg: '#ede9fe' },
  { value: 'membre',            label: 'Membre',            color: '#3B6D11', bg: '#f0f8e1' },
  { value: 'resilie',           label: 'Résilié',           color: '#ef4444', bg: '#fef2f2' },
  { value: 'exclu',             label: 'Exclu',             color: '#1c1c1e', bg: '#f2f2f7' },
];

function stringToColor(str) {
  const colors = ['#95C11F','#3C2618','#7a9e18','#2563eb','#7c3aed','#0891b2','#059669'];
  let hash = 0;
  for (let i = 0; i < (str||'').length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function formatCA(n) {
  if (!n || n === 0) return '0€';
  if (n >= 1000000) return `${(n/1000000).toFixed(1)}M€`;
  if (n >= 1000) return `${Math.round(n/1000)}k€`;
  return `${Math.round(n)}€`;
}

// Formate une date ISO en YYYY-MM-DD pour les inputs
function toDateInput(val) {
  if (!val) return '';
  return val.split('T')[0];
}

export default function ContactsPage() {
  const { api, currentGroupeId } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtre, setFiltre] = useState('tous');
  const [view, setView] = useState('list'); // list | form | detail
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadContacts = useCallback(() => {
    setLoading(true);
    const params = {};
    if (currentGroupeId) params.groupe_id = currentGroupeId;
    if (filtre !== 'tous') params.statut = filtre;
    api.get('/contacts', { params })
      .then(r => setContacts(r.data))
      .catch(() => setContacts([]))
      .finally(() => setLoading(false));
  }, [api, currentGroupeId, filtre]);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (selected?.id) {
        await api.put(`/contacts/${selected.id}`, formData);
        toast.success('Contact mis à jour !');
      } else {
        await api.post('/contacts', { ...formData, groupe_id: currentGroupeId });
        toast.success('Contact créé !');
      }
      loadContacts();
      setView('list');
      setSelected(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce contact ? Cette action est irréversible.')) return;
    try {
      await api.delete(`/contacts/${id}`);
      toast.success('Contact supprimé');
      loadContacts();
      setView('list');
      setSelected(null);
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  if (view === 'form') return (
    <ContactForm
      contact={selected}
      onSave={handleSave}
      onCancel={() => { setView('list'); setSelected(null); }}
      saving={saving}
    />
  );

  if (view === 'detail' && selected) return (
    <ContactDetail
      contact={selected}
      onBack={() => { setView('list'); setSelected(null); }}
      onEdit={() => setView('form')}
      onDelete={() => handleDelete(selected.id)}
      api={api}
      onRefresh={loadContacts}
    />
  );

  const filtered = contacts.filter(c =>
    !search ||
    c.nom?.toLowerCase().includes(search.toLowerCase()) ||
    c.prenom?.toLowerCase().includes(search.toLowerCase()) ||
    c.societe?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ minHeight: '100vh' }}>
      <div style={{ padding: '12px 20px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1c1c1e' }}>
          Contacts <span style={{ fontSize: 15, color: '#8e8e93', fontWeight: 400 }}>({filtered.length})</span>
        </h1>
        <button onClick={() => { setSelected(null); setView('form'); }} className="mb-btn mb-btn-primary mb-btn-sm">
          + Nouveau
        </button>
      </div>

      <div className="mb-content" style={{ paddingTop: 0 }}>
        {/* Filtres statut */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, overflowX: 'auto', paddingBottom: 2 }}>
          <button onClick={() => setFiltre('tous')} className="mb-btn mb-btn-pill"
            style={{ background: filtre === 'tous' ? '#95C11F' : 'white', color: filtre === 'tous' ? 'white' : '#8e8e93', border: '0.5px solid #d1d1d6', whiteSpace: 'nowrap' }}>
            Tous ({contacts.length})
          </button>
          {STATUTS.map(s => {
            const count = contacts.filter(c => c.statut === s.value).length;
            if (count === 0 && filtre !== s.value) return null;
            return (
              <button key={s.value} onClick={() => setFiltre(s.value)} className="mb-btn mb-btn-pill"
                style={{ background: filtre === s.value ? s.color : 'white', color: filtre === s.value ? 'white' : s.color, border: `0.5px solid ${s.color}`, whiteSpace: 'nowrap' }}>
                {s.label} ({count})
              </button>
            );
          })}
        </div>

        {/* Recherche */}
        <div style={{ background: 'white', borderRadius: 12, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8e8e93" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: 14, color: '#1c1c1e', background: 'transparent' }}
            placeholder="Nom, prénom, société, email..." />
          {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8e8e93', fontSize: 20 }}>×</button>}
        </div>

        {loading ? (
          <div className="mb-loading"><div className="mb-spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="mb-empty mb-card">
            <div className="mb-empty-title">Aucun contact</div>
            <div className="mb-empty-sub">Cliquez sur "+ Nouveau" pour créer votre premier contact.</div>
          </div>
        ) : (
          filtered.map(c => {
            const s = STATUTS.find(st => st.value === c.statut) || STATUTS[0];
            return (
              <div key={c.id} className="mb-member-card" style={{ marginBottom: 8 }}
                onClick={() => { setSelected(c); setView('detail'); }}>
                <div className="mb-member-avatar" style={{ background: stringToColor(c.nom) }}>
                  {c.photo_url ? <img src={c.photo_url} alt="" /> : `${c.prenom?.[0]||''}${c.nom?.[0]||''}`}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1c1c1e' }}>{c.nom} {c.prenom}</div>
                  <div style={{ fontSize: 12, color: '#8e8e93', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.societe || c.metier_represente || c.email}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 6, background: s.bg, color: s.color }}>{s.label}</span>
                    {c.date_entree && <span style={{ fontSize: 10, color: '#8e8e93' }}>Depuis {new Date(c.date_entree).toLocaleDateString('fr-FR')}</span>}
                  </div>
                </div>
                <svg width="8" height="14" viewBox="0 0 8 14" fill="none" stroke="#c7c7cc" strokeWidth="2"><path d="M1 1l6 6-6 6"/></svg>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Formulaire création / édition
// ─────────────────────────────────────────────
function ContactForm({ contact, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    civilite:                  contact?.civilite || 'M.',
    nom:                       contact?.nom || '',
    prenom:                    contact?.prenom || '',
    email:                     contact?.email || '',
    mobile:                    contact?.mobile || '',
    telephone:                 contact?.telephone || '',
    societe:                   contact?.societe || '',
    metier_represente:         contact?.metier_represente || '',
    poste:                     contact?.poste || '',
    description:               contact?.description || '',
    adresse_bureau:            contact?.adresse_bureau || '',
    site_web:                  contact?.site_web || '',
    lien_facebook:             contact?.lien_facebook || '',
    lien_linkedin:             contact?.lien_linkedin || '',
    lien_instagram:            contact?.lien_instagram || '',
    statut:                    contact?.statut || 'membre',
    date_entree:               toDateInput(contact?.date_entree),
    date_fin_adhesion:         toDateInput(contact?.date_fin_adhesion),
    montant_cotisation:        contact?.montant_cotisation || '',
    date_prochaine_invitation: toDateInput(contact?.date_prochaine_invitation),
    commentaire:               contact?.commentaire || '',
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = () => {
    if (!form.nom.trim()) return toast.error('Le nom est obligatoire');
    if (!form.prenom.trim()) return toast.error('Le prénom est obligatoire');
    onSave(form);
  };

  const SECTIONS = [
    {
      title: 'Identité',
      fields: [
        { label: 'Civilité', key: 'civilite', type: 'select', options: ['M.','Mme'] },
        { label: 'Nom *', key: 'nom', placeholder: 'Dupont' },
        { label: 'Prénom *', key: 'prenom', placeholder: 'Jean' },
        { label: 'Email', key: 'email', type: 'email', placeholder: 'jean@exemple.com' },
        { label: 'Mobile', key: 'mobile', type: 'tel', placeholder: '06 12 34 56 78' },
        { label: 'Téléphone fixe', key: 'telephone', type: 'tel', placeholder: '03 20 00 00 00' },
      ],
    },
    {
      title: 'Société',
      fields: [
        { label: 'Nom de la société', key: 'societe', placeholder: 'Dupont Conseil' },
        { label: 'Métier représenté', key: 'metier_represente', placeholder: 'Consultant digital', maxLength: 50 },
        { label: 'Poste', key: 'poste', placeholder: 'Gérant' },
        { label: 'Présentation (300 car. max)', key: 'description', type: 'textarea', placeholder: "Décrivez l'activité...", maxLength: 300 },
        { label: 'Adresse bureau', key: 'adresse_bureau', placeholder: '45 rue Nationale, 59000 Lille' },
        { label: 'Site web', key: 'site_web', type: 'url', placeholder: 'https://www.exemple.com' },
      ],
    },
    {
      title: 'Réseaux sociaux',
      fields: [
        { label: 'Facebook', key: 'lien_facebook', type: 'url', placeholder: 'https://facebook.com/...' },
        { label: 'LinkedIn', key: 'lien_linkedin', type: 'url', placeholder: 'https://linkedin.com/in/...' },
        { label: 'Instagram', key: 'lien_instagram', type: 'url', placeholder: 'https://instagram.com/...' },
      ],
    },
    {
      title: 'Statut & Adhésion',
      fields: [
        { label: 'Statut', key: 'statut', type: 'select', options: STATUTS.map(s => ({ value: s.value, label: s.label })) },
        { label: "Date d'entrée", key: 'date_entree', type: 'date' },
        { label: "Fin d'adhésion", key: 'date_fin_adhesion', type: 'date' },
        { label: 'Montant cotisation (€)', key: 'montant_cotisation', type: 'number', placeholder: '250' },
        { label: 'Prochaine invitation', key: 'date_prochaine_invitation', type: 'date' },
        { label: 'Commentaire interne', key: 'commentaire', type: 'textarea', placeholder: "Note visible uniquement par l'animateur..." },
      ],
    },
  ];

  return (
    <div style={{ minHeight: '100vh' }}>
      <div style={{ padding: '12px 20px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={onCancel} style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#95C11F', fontSize: 15, fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer' }}>
          <svg width="10" height="16" viewBox="0 0 10 16" fill="none" stroke="#95C11F" strokeWidth="2.5"><path d="M8 2L2 8l6 6"/></svg>
          Contacts
        </button>
        <h1 style={{ fontSize: 17, fontWeight: 700, color: '#1c1c1e' }}>{contact ? 'Modifier le contact' : 'Nouveau contact'}</h1>
        <button onClick={handleSubmit} disabled={saving} className="mb-btn mb-btn-primary mb-btn-sm">
          {saving ? '...' : 'Enregistrer'}
        </button>
      </div>

      <div className="mb-content" style={{ paddingTop: 0 }}>
        {SECTIONS.map(section => (
          <div key={section.title} className="mb-card mb-mb-md">
            <div className="mb-card-title">{section.title}</div>
            {section.fields.map(f => (
              <div key={f.key} className="mb-field">
                <label className="mb-label">{f.label}</label>
                {f.type === 'select' ? (
                  <select value={form[f.key]} onChange={e => set(f.key, e.target.value)} className="mb-select">
                    {f.options.map(o =>
                      typeof o === 'string'
                        ? <option key={o} value={o}>{o}</option>
                        : <option key={o.value} value={o.value}>{o.label}</option>
                    )}
                  </select>
                ) : f.type === 'textarea' ? (
                  <textarea value={form[f.key]} onChange={e => set(f.key, e.target.value)}
                    className="mb-input" rows={3} placeholder={f.placeholder} maxLength={f.maxLength} />
                ) : (
                  <input type={f.type || 'text'} value={form[f.key]}
                    onChange={e => set(f.key, e.target.value)}
                    className="mb-input" placeholder={f.placeholder} maxLength={f.maxLength} />
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Fiche détail contact
// ─────────────────────────────────────────────
function ContactDetail({ contact, onBack, onEdit, onDelete, api, onRefresh }) {
  const [stats, setStats] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    api.get(`/contacts/${contact.id}/stats`).then(r => setStats(r.data)).catch(() => {});
  }, [contact.id, api]);

  const statut = STATUTS.find(s => s.value === contact.statut) || STATUTS[0];
  const initials = `${contact.prenom?.[0]||''}${contact.nom?.[0]||''}`.toUpperCase();

  const handleStatutChange = async (newStatut) => {
    try {
      await api.put(`/contacts/${contact.id}`, { statut: newStatut });
      toast.success('Statut mis à jour !');
      onRefresh();
      onBack();
    } catch { toast.error('Erreur lors du changement de statut'); }
  };

  return (
    <div style={{ minHeight: '100vh' }}>
      <div style={{ padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#95C11F', fontSize: 15, fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer' }}>
          <svg width="10" height="16" viewBox="0 0 10 16" fill="none" stroke="#95C11F" strokeWidth="2.5"><path d="M8 2L2 8l6 6"/></svg>
          Contacts
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onEdit} className="mb-btn mb-btn-ghost mb-btn-sm">Modifier</button>
          <button onClick={() => setConfirmDelete(true)} className="mb-btn mb-btn-sm" style={{ background: '#fef2f2', color: '#ef4444' }}>
            Supprimer
          </button>
        </div>
      </div>

      <div className="mb-content" style={{ paddingTop: 0 }}>
        {/* Hero */}
        <div className="mb-hero mb-mb-md">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div className="mb-hero-avatar" style={{ background: stringToColor(contact.nom) }}>
              {contact.photo_url
                ? <img src={contact.photo_url} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }} />
                : initials}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="mb-hero-name">{contact.prenom} {contact.nom}</div>
              <div className="mb-hero-sub">{contact.metier_represente || contact.poste}</div>
              <div className="mb-hero-company">{contact.societe}</div>
              <span style={{ display: 'inline-block', marginTop: 6, fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20, background: statut.bg, color: statut.color }}>
                {statut.label}
              </span>
            </div>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="mb-stat-grid mb-mb-md">
            {[
              { label: 'CA généré', val: formatCA(stats.mpb_donnes?.montant), color: '#95C11F' },
              { label: 'CA reçu',   val: formatCA(stats.mpb_recus?.montant),  color: '#3C2618' },
              { label: 'Présence',  val: `${stats.taux_presence}%`, color: stats.taux_presence < 75 ? '#ef4444' : '#95C11F' },
              { label: 'TAT',       val: stats.nb_tats,             color: '#1c1c1e' },
            ].map(s => (
              <div key={s.label} className="mb-stat-card">
                <div className="mb-stat-accent" style={{ background: s.color }} />
                <div className="mb-stat-label">{s.label}</div>
                <div className="mb-stat-value" style={{ fontSize: 20, color: s.color }}>{s.val}</div>
              </div>
            ))}
          </div>
        )}

        {/* Changer statut */}
        <div className="mb-card mb-mb-md">
          <div className="mb-card-title">Changer le statut</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {STATUTS.map(s => (
              <button key={s.value} onClick={() => handleStatutChange(s.value)}
                style={{ padding: '6px 12px', borderRadius: 20, border: `1.5px solid ${s.color}`, background: contact.statut === s.value ? s.color : 'white', color: contact.statut === s.value ? 'white' : s.color, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Coordonnées */}
        <div className="mb-card mb-mb-md">
          <div className="mb-card-title">Coordonnées</div>
          {[
            { label: 'Mobile',       value: contact.mobile,       href: `tel:${contact.mobile}` },
            { label: 'Téléphone',    value: contact.telephone,    href: `tel:${contact.telephone}` },
            { label: 'Email',        value: contact.email,        href: `mailto:${contact.email}`, small: true },
            { label: 'Adresse',      value: contact.adresse_bureau },
            { label: 'Site web',     value: contact.site_web,     href: contact.site_web, small: true },
            { label: 'Parrain',      value: contact.parrain_nom ? `${contact.parrain_prenom} ${contact.parrain_nom}` : null },
            { label: 'Entrée',       value: contact.date_entree ? new Date(contact.date_entree).toLocaleDateString('fr-FR') : null },
            { label: 'Fin adhésion', value: contact.date_fin_adhesion ? new Date(contact.date_fin_adhesion).toLocaleDateString('fr-FR') : null },
            { label: 'Proch. invit.', value: contact.date_prochaine_invitation ? new Date(contact.date_prochaine_invitation).toLocaleDateString('fr-FR') : null },
          ].filter(r => r.value).map(r => (
            <div key={r.label} className="mb-info-row">
              <div className="mb-info-ico">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#95C11F" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>
              </div>
              <span className="mb-info-label">{r.label}</span>
              {r.href
                ? <a href={r.href} className="mb-info-value mb-info-link" style={r.small?{fontSize:12}:{}}>{r.value}</a>
                : <span className="mb-info-value" style={r.small?{fontSize:12}:{}}>{r.value}</span>}
            </div>
          ))}
        </div>

        {/* Commentaire */}
        {contact.commentaire && (
          <div className="mb-card mb-mb-md">
            <div className="mb-card-title">Note interne</div>
            <p style={{ fontSize: 13, color: '#3c3c43', lineHeight: 1.6 }}>{contact.commentaire}</p>
          </div>
        )}

        {/* Présentation */}
        {contact.description && (
          <div className="mb-card mb-mb-md">
            <div className="mb-card-title">Présentation</div>
            <p style={{ fontSize: 13, color: '#3c3c43', lineHeight: 1.6 }}>{contact.description}</p>
          </div>
        )}
      </div>

      {/* Modal confirmation suppression */}
      {confirmDelete && (
        <div onClick={() => setConfirmDelete(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: '24px 20px', width: '100%' }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#1c1c1e', textAlign: 'center', marginBottom: 8 }}>Supprimer ce contact ?</div>
            <div style={{ fontSize: 14, color: '#8e8e93', textAlign: 'center', marginBottom: 20 }}>
              {contact.prenom} {contact.nom} sera désactivé et ne sera plus visible dans l'application.
            </div>
            <button onClick={onDelete} style={{ width: '100%', padding: 14, borderRadius: 12, background: '#ef4444', border: 'none', fontSize: 15, fontWeight: 600, color: 'white', cursor: 'pointer', marginBottom: 10 }}>
              Confirmer la suppression
            </button>
            <button onClick={() => setConfirmDelete(false)} style={{ width: '100%', padding: 14, borderRadius: 12, background: '#f2f2f7', border: 'none', fontSize: 15, fontWeight: 600, color: '#1c1c1e', cursor: 'pointer' }}>
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
